import { useState } from 'react';

import { useNewStrategyContext } from './NewStrategyContext';

import { Icon } from '../UI';
import { CreateAsset } from './CreateAsset';
import { ProfitLoss } from '../ProfitLoss';
import { Title } from './Title';

import {
  computePercentage,
  getSymbol,
  getTotalValues,
} from '../../helpers/functions';

import { Category as CategoryType } from './types';
import { IStrategyAsset, TTickerCurrency } from '../../types/model';
import { Currency } from '../../types/common';
import { AccordionButton } from './AccordionButton';
import { CustomNumber } from '../CustomNumber/CustomNumber';
import moment from 'moment';

export const Category = ({
  isNewStrategy,
  category: { assets: categoryAssets, title },
  currency,
  totalValueStrategy,
}: {
  category: CategoryType;
  currency: Currency['code'];
  isNewStrategy: boolean;
  totalValueStrategy: number;
}) => {
  const { newStrategy, totalValueNewStrategy, dispatch } =
    useNewStrategyContext();

  const [showAssets, setShowAssets] = useState(true);

  const { assets: newStrategyAssets } = newStrategy;
  const newAssetsIds = newStrategyAssets.map(({ id }) => id);
  const selectedAllAssets =
    !isNewStrategy &&
    categoryAssets.every(({ id }) => newAssetsIds.includes(id));
  const svgName = isNewStrategy ? 'trash' : 'plus';
  const symbol = getSymbol(currency);
  const { percent: categoryPercent, totalValue: categoryValue } =
    getTotalValues(
      categoryAssets,
      currency.toLocaleLowerCase() as TTickerCurrency,
      moment().format('YYYY-MM-DD')
    );
  const total = isNewStrategy ? totalValueNewStrategy : totalValueStrategy;
  const allocation = computePercentage(total, categoryValue);

  const handleDropdown = () => setShowAssets(!showAssets);

  const handleAssets = (selectedAssets: IStrategyAsset[]) => {
    let newAssets = [...newStrategyAssets];

    selectedAssets.forEach((asset) => {
      isNewStrategy
        ? // remove asset
          (newAssets = newAssets.filter(({ id }) => id !== asset.id))
        : // add assets only if they don't already exist
          !newAssetsIds.includes(asset.id) && newAssets.push(asset);
    });

    dispatch({ newStrategy: { ...newStrategy, assets: newAssets } });
  };

  return (
    <>
      <div
        className={`
          grid
          grid-cols-6
          items-center
          pl-5
          min-h-16
          border-b
          border-grey-30
          dark:border-grey-70
        `}
      >
        <Title
          className="col-span-4"
          title={title}
          prefix={
            <AccordionButton
              isOpen={showAssets}
              handleDropdown={handleDropdown}
            />
          }
        />

        {/* tot value */}
        <ProfitLoss
          percentage={isNewStrategy ? null : categoryPercent}
          symbol={symbol!}
          value={categoryValue}
        />

        {/* percentage invested */}
        <div className="flex items-center justify-between">
          <CustomNumber
            allowNegative={false}
            className="w-3/4"
            decimalScale={2}
            suffix={'%'}
            value={allocation}
          />
          <Icon
            className={`
              mr-5
              ${selectedAllAssets ? 'cursor-not-allowed' : 'cursor-pointer'}
            `}
            onClick={() => handleAssets(categoryAssets)}
            svgName={svgName}
            color={selectedAllAssets ? 'grey-30' : undefined}
          />
        </div>
      </div>
      {showAssets &&
        categoryAssets.map((asset) => (
          <CreateAsset
            key={asset.id}
            asset={asset}
            currency={currency}
            isNewStrategy={isNewStrategy}
            handleAsset={() => handleAssets([asset])}
            svgName={svgName}
            totalValueStrategy={totalValueStrategy}
          />
        ))}
    </>
  );
};
